import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { compact, filter, flatMap, isEmpty, map, orderBy, some, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  View,
  Box,
  FlatList,
  useTheme,
  VStack,
  HStack,
  Divider,
  Pressable,
  CheckIcon,
  Checkbox,
  ArrowDownIcon,
  ArrowUpIcon,
} from 'native-base';
import { useBoolean } from 'usehooks-ts';

import { Button, Modal, Text } from '@pimm/base';
import { MarkTrainingComplete } from '@pimm/services/lib/sms-workforce/services';
import { ResourceLoader } from '@app/components/shared';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { useSiteConfig } from '@app/features/store-core';
import { formatName } from '@app/utils/string-formatter';
import { SmsRoles } from '@app/features/store-info/hooks';
import { EmpLearningPathStatusEnum, MarkTrainingCompleteRequest } from '@pimm/services/lib/sms-workforce';
import { DropdownLearningPaths } from './drowndown-learning-paths';
import { useGetHubStats } from '../hooks';
import { LearningPathBadge } from './learning-path-badge';
import { EditStatusConfirmation } from './edit-status-confirmation';

type EditStatusProps = {
  isAuthorized?: boolean;
  queryHubStats: ReturnType<typeof useGetHubStats>;
  onConfirm?: () => (_confirm?: boolean) => void;
};

export const EditStatus = ({ isAuthorized, queryHubStats, ..._props }: EditStatusProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const modalConfirm = useModalFocus();
  const isLearnerDesc = useBoolean(false);
  const [learningPathId, setLearningPathId] = useState<string>();
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);

  const markTrainingComplete = useMutation({
    mutationFn: MarkTrainingComplete,
    onSuccess: () => {
      queryHubStats.refetch();
      setEmployeeIds([]);
    },
  });

  const handleConfirmChanges = () => {
    modalConfirm.setHide();
    if (learningPathId) {
      const payload: MarkTrainingCompleteRequest = {
        learningPathId,
        siteId: siteConfig.id!,
        employeeIds,
      };

      markTrainingComplete.mutate(payload);
    }
  };

  const filteredUsers = useMemo(() => {
    const users = filter(queryHubStats.data, user =>
      some(user.learningPathStats, lp => lp.learningPathId === learningPathId && lp.learningStatus !== EmpLearningPathStatusEnum.Complete),
    );

    const sortOrder = isLearnerDesc.value ? 'desc' : 'asc';

    return orderBy(
      users,
      [
        _ => {
          const idx = [SmsRoles.MANAGER, SmsRoles.USER, SmsRoles.EMPLOYEE].indexOf(_.role?.trim()?.toUpperCase() as SmsRoles);
          return idx === -1 ? 99 : idx;
        },
        _ => _.firstName?.toLowerCase(),
        _ => _.lastName?.toLowerCase(),
      ],
      [sortOrder, sortOrder, sortOrder],
    );
  }, [queryHubStats.data, learningPathId, isLearnerDesc.value]);

  const allIds = useMemo(() => compact(filteredUsers.map(u => u.id)), [filteredUsers]);

  const toggleOne = (id?: string) => {
    if (!id) return;
    setEmployeeIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const toggleAll = (checked: boolean) => setEmployeeIds(checked ? allIds.slice() : []);

  const learningPaths = useMemo(() => {
    if (!queryHubStats?.isSuccess) return [];

    return uniqBy(
      compact(
        map(flatMap(queryHubStats.data, 'learningPathStats'), lp => lp?.learningPathId && { id: lp.learningPathId, name: lp.learningPath }),
      ),
      'id',
    );
  }, [queryHubStats?.isSuccess, queryHubStats.data]);

  useEffect(() => {
    if (isEmpty(learningPathId) && !isEmpty(learningPaths)) {
      setLearningPathId(learningPaths?.[0]?.id);
    }
  }, [learningPaths]);

  return (
    <View position="relative" pt={5} minHeight="200px">
      <VStack px={6} space={3} pb={5}>
        <Box>
          <Text size="lg" color="gray.800" fontWeight={600}>
            {siteConfig.name}
          </Text>
          <Box flex={1}>
            <DropdownLearningPaths options={learningPaths} value={learningPathId} onChange={setLearningPathId} />
          </Box>
        </Box>

        <FlatList
          w="full"
          minH={200}
          maxH={600}
          data={filteredUsers}
          borderColor="gray.200"
          borderWidth={1}
          borderRadius="lg"
          initialNumToRender={25}
          stickyHeaderIndices={[0]}
          scrollEnabled
          ItemSeparatorComponent={() => <Divider bgColor="gray.200" />}
          ListHeaderComponent={() => (
            <HStack
              w="full"
              bgColor="gray.200"
              flex={1}
              borderLeftColor="gray.300"
              borderLeftWidth={1}
              space={1}
              alignItems="center"
              minHeight={8}
            >
              <Box flex={1.5} justifyContent="center" px={3}>
                <HStack space={5}>
                  <Checkbox
                    value="__all__"
                    size="sm"
                    isChecked={employeeIds.length > 0 && employeeIds.length === allIds.length}
                    isIndeterminate={employeeIds.length > 0 && employeeIds.length < allIds.length}
                    onChange={toggleAll}
                    accessibilityLabel="Select all learners"
                  />
                  <Pressable onPress={() => isLearnerDesc.toggle()}>
                    <HStack space={2} alignItems="center" justifyContent="center">
                      <Text color="gray.600" fontWeight={500}>
                        {t('app_insights:learner')}
                      </Text>

                      {isLearnerDesc.value ? <ArrowDownIcon size={3} color="gray.600" /> : <ArrowUpIcon size={3} color="gray.600" />}
                    </HStack>
                  </Pressable>
                </HStack>
              </Box>
              <Box flex={0.7} justifyContent="center" py={1.5} pl={5} pr={3}>
                <Text color="gray.600" fontWeight={500}>
                  {t('app_insights:role')}
                </Text>
              </Box>
              <Box flex={0.8} justifyContent="center" py={1.5} pl={5} pr={3} alignItems="flex-start">
                <Text color="gray.600" fontWeight={500}>
                  {t('app_insights:status')}
                </Text>
              </Box>
              <Box flex={0.8} justifyContent="center" py={1.5} pl={5} pr={3}>
                <Text color="gray.600" fontWeight={500}>
                  {t('app_insights:last_activity')}
                </Text>
              </Box>
            </HStack>
          )}
          ListEmptyComponent={() => (
            <ResourceLoader
              w="full"
              alignItems="center"
              justifyContent="center"
              minH="200px"
              isLoading={queryHubStats.isLoading}
              isEmpty={!queryHubStats.isLoading && isEmpty(filteredUsers)}
              emptyMessage={t('app_insights:no_available_learner')}
            />
          )}
          renderItem={({ item, index }) => {
            const learningPath = item?.learningPathStats?.find(_ => _?.learningPathId === learningPathId);
            const roleName = item?.role === SmsRoles.MANAGER ? t('app_insights:manager') : t('app_insights:user');

            return (
              <HStack minHeight={8} bgColor="white" key={`${item.id}-${index}`}>
                <Box flex={1.5} justifyContent="center" py={1.5} px={3} bgColor="white" borderRightWidth={1} borderRightColor="gray.200">
                  <HStack space={5} alignItems="center">
                    <Checkbox
                      size="sm"
                      value={item.id ?? ''}
                      isChecked={!!item?.id && employeeIds.includes(item?.id)}
                      onChange={() => toggleOne(item.id)}
                      accessibilityLabel={`Select ${formatName(item.firstName, item.lastName)}`}
                    />
                    <Text color="gray.800" size="sm" fontWeight={500}>
                      {formatName(item.firstName, item.lastName)}
                    </Text>
                  </HStack>
                </Box>
                <Box
                  flex={0.7}
                  justifyContent="center"
                  py={1.5}
                  pl={5}
                  pr={3}
                  bgColor="white"
                  borderRightWidth={1}
                  borderRightColor="gray.200"
                >
                  <Text color="gray.800" size="sm" fontWeight={500}>
                    {roleName}
                  </Text>
                </Box>
                <Box
                  flex={0.8}
                  justifyContent="center"
                  py={1.5}
                  pl={5}
                  pr={3}
                  alignItems="flex-start"
                  bgColor="white"
                  borderRightWidth={1}
                  borderRightColor="gray.200"
                >
                  <LearningPathBadge status={learningPath?.learningStatus ?? EmpLearningPathStatusEnum.Pending} />
                </Box>
                <Box
                  flex={0.8}
                  justifyContent="center"
                  py={1.5}
                  pl={5}
                  pr={3}
                  bgColor="white"
                  borderRightWidth={1}
                  borderRightColor="gray.200"
                >
                  <Text color="gray.800" size="sm" fontWeight={500}>
                    {learningPath?.lastActivityDaysAgo
                      ?.toLowerCase()
                      .replace('no activity', t('app_insights:no_activity'))
                      .replace('days ago', t('app_insights:days_ago'))}
                  </Text>
                </Box>
              </HStack>
            );
          }}
        />
      </VStack>
      <Box alignItems="flex-end" py={2.5} px={4} borderTopWidth={1}>
        <Button
          minWidth={100}
          endIcon={<CheckIcon size="sm" color="black" />}
          isLoading={markTrainingComplete.isLoading}
          isLoadingText={t('app_insights:mark_complete')}
          disabled={isEmpty(employeeIds)}
          onPress={modalConfirm.setOpen}
        >
          {t('app_insights:mark_complete')}
        </Button>
      </Box>

      <Modal size="md" _content={{ rounded: '2xl', maxW: 400 }} isOpen={modalConfirm.isOpen} noPadding onClose={modalConfirm.setHide}>
        <EditStatusConfirmation
          isAuthorized
          onClose={modalConfirm.setHide}
          selectedUserCount={employeeIds?.length}
          onConfirm={handleConfirmChanges}
        />
      </Modal>
    </View>
  );
};
